/**
 * ReallyAvatar ↔ ReallyAssess integration contract
 *
 * ReallyAssess loads ReallyAvatar as the assessment iframe.
 * When ReallyAvatar finishes, it signals completion; ReallyAssess
 * finalizes the integrity score and POSTs it to the configured webhook.
 */

/** Query params ReallyAssess appends to the ReallyAvatar assessment URL */
export const REALLYASSESS_URL_PARAMS = {
  sessionId: 'sessionId',
  userId: 'userId',
  userEmail: 'userEmail',
  userName: 'userName',
} as const

/** postMessage from ReallyAvatar iframe → ReallyAssess parent */
export interface ReallyAvatarCompletionMessage {
  type: 'really-avatar:assessment-complete'
  version: 1
  sessionId: string
}

/** Legacy / alternate completion message shapes */
export const ASSESSMENT_COMPLETION_MESSAGE_TYPES = [
  'really-avatar:assessment-complete',
  'reallyassess-assessment-complete',
  'assessment-complete',
] as const

export function isAssessmentCompletionMessage(
  data: unknown,
  expectedSessionId?: string
): boolean {
  if (!data || typeof data !== 'object') return false

  const record = data as Record<string, unknown>
  const type = record.type ?? record.event
  const sessionId = record.sessionId

  const typeMatches =
    type === 'really-avatar:assessment-complete' ||
    type === 'reallyassess-assessment-complete' ||
    type === 'assessment-complete' ||
    record.event === 'assessment.completed'

  if (!typeMatches) return false

  if (expectedSessionId && typeof sessionId === 'string' && sessionId.length > 0) {
    return sessionId === expectedSessionId
  }

  return true
}

/**
 * Face API endpoint ReallyAvatar calls when assessment is finished.
 * POST {faceApiUrl}/api/sessions/{sessionId}/complete
 * Header: X-Completion-Secret
 */
export const REALLYASSESS_COMPLETE_API_PATH = '/api/sessions/:sessionId/complete'

/**
 * Face API endpoint ReallyAvatar calls to fetch integrity score.
 * GET {faceApiUrl}/api/sessions/{sessionId}/integrity
 * Header: X-Completion-Secret
 */
export const REALLYASSESS_INTEGRITY_API_PATH = '/api/sessions/:sessionId/integrity'
